import React from 'react'
import classes from './InfoCards.styles'
import { Grid, Typography, Box } from '@material-ui/core'
import { InfoCardProps } from './InfoCards'
import StyledButton from '../StyledButton/StyledButton'
import { cx } from 'linaria'
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos'
import router from 'next/router'
import Wysiwyg from '../Wysiwyg/Wysiwyg'
import useIntersectionObserver from '../EnquiryBottomBar.js/EnquiryHook'

const InfoCard = ({ card, key }: { card: InfoCardProps; key: number }) => {
  const [open, setOpen] = React.useState<boolean>(false)

  const cardRef = React.useRef<HTMLDivElement>(null)

  const [isVisible] = useIntersectionObserver({
    elementRef: cardRef,
    horizontalOnly: true,
  })

  const flipCard = open
    ? cx(classes.flipcard.inner, classes.flipcard.cardFlipped)
    : cx(classes.flipcard.inner);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault()
      setOpen(!open)
    }
  }

  return (
    <div
      className={classes.cardOuter}
      key={key}
      ref={cardRef}
      onClick={() => setOpen(!open)}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={isVisible ? (!open ? 0 : -1) : -1}
      aria-expanded={open ? "true" : "false"}
      aria-label={card.front_title || "Info card"}
    >
      <Box className={flipCard}>
        <Box className={classes.flipCardInner}>
          <Grid
            container
            direction="column"
            justify="center"
            style={{ backgroundImage: `url(${card.image})` }}
            className={cx(
              classes.flipcard.cardSide,
              classes.flipcard.front,
              card.image_overlay && classes.overlayWrapper,
              card.enable_cta_area && classes.flipcard.cardShortSide
            )}
            role="region"
            aria-label="Card front"
            aria-hidden={open || !isVisible ? "true" : "false"}
          >
            {card.front_title && (
              <Typography variant="h3" tabIndex={isVisible && !open ? 0 : -1}>
                {card.front_title}
              </Typography>
            )}
            {card.front_button_text && (
              <button
                className={classes.frontButton}
                type="button"
                aria-label={card.front_button_text}
                tabIndex={isVisible && !open ? 0 : -1}
              >
                <Typography color="primary" variant="body1">
                  {card.front_button_text}
                </Typography>
                <img
                  src={card.front_icon}
                  alt={card.front_button_text || "Front button icon"}
                />
              </button>
            )}
          </Grid>
          {!open && card.read_more_text && card.cta_button && (
            <Box className={classes.frontReadButton}>
              {card.cta_button?.url && (
                <StyledButton
                  color="primary"
                  isModal={card.cta_button.modal}
                  url={card.cta_button.url}
                  isFullWidth
                  aria-label={card.cta_button.text}
                  tabIndex={isVisible && !open ? 0 : -1}
                >
                  {card.cta_button.text}
                  {card.cta_button.icon && (
                    <img
                      src={card.cta_button.icon}
                      alt={card.cta_button.text || "CTA button icon"}
                    />
                  )}
                </StyledButton>
              )}
              <Typography
                color="primary"
                variant="body1"
                className={cx(!card.enable_cta_area && classes.readMoreOverlay)}
                tabIndex={isVisible && !open ? 0 : -1}
              >
                {card.read_more_text}
              </Typography>
            </Box>
          )}
        </Box>
        <Grid
          container
          className={cx(classes.flipcard.cardSide, classes.flipcard.back)}
          role="region"
          aria-label="Card back"
          aria-hidden={!open || !isVisible ? "true" : "false"}
        >
          <Box mt={8}>
            {card.back_title && (
              <Typography variant="body2" tabIndex={isVisible && open ? 0 : -1}>
                {card.back_title}
              </Typography>
            )}
            {card.back_body && (
              <Typography
                variant="caption"
                role="text"
                tabIndex={isVisible && open ? 0 : -1}
                aria-label={card.back_body}
              >
                {card.back_body}
              </Typography>
            )}
            {card.card_body && (
              <Wysiwyg
                className={classes.flipcard.features}
                body={card.card_body}
                variant="body2"
                tabIndex={isVisible && open ? 0 : -1}
              />
            )}
          </Box>
          <Grid
            container
            justify="space-between"
            className={classes.backButtonContainer}
          >
            {card.back_icon && (
              <img
                src={card.back_icon}
                alt={card.front_button_text || "Back icon"}
                tabIndex={isVisible && open ? 0 : -1}
              />
            )}
            {card.back_button_text && (
              <button
                type="button"
                onClick={event => {
                  event.stopPropagation()
                  card.back_button_url && router.push(card.back_button_url)
                }}
                aria-label={card.back_button_text}
                tabIndex={isVisible && open ? 0 : -1}
              >
                <Typography variant="body1">{card.back_button_text}</Typography>
                <ArrowForwardIosIcon color="error" />
              </button>
            )}
          </Grid>
        </Grid>
      </Box>
    </div>
  )
}

export default InfoCard
